import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LogoutIcon from '@mui/icons-material/Logout';
import DynamicMenu from '../menu/sidemenu/DynamicMenu';
import { 
  Box, 
  styled, 
  type Theme,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';

const drawerWidth = 240;
const miniDrawerWidth = 60;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      width: miniDrawerWidth,
    },
  },
}));

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2),
  minHeight: 64,
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const LogoContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  '& img': {
    maxHeight: 40,
    width: 'auto',
    objectFit: 'contain',
  },
});

const DrawerContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const MenuContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
});

const UserSection = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  padding: 0,
}));

const UserButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const LogoutButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1, 2, 1, 4),
  color: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const UserInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  minWidth: 0,
});

const UserName = styled(Typography)(({ theme }) => ({
  marginLeft: theme.spacing(1.5),
  fontWeight: 500,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

class LeftDrawer extends Component<any,any> {
  static propTypes: any;
  static defaultProps: any;
  constructor(props:any) {
    super(props);
    this.state = {
      selectedItem: null,
      userMenuOpen: false
    };
  }

  menuname=(menu:string)=>{
    return menu.replace('/','');
  }

  handleClick = (menu:any,page:any,index:any)=> () => {
    console.log('handleclick:menu,page,index',menu,page,index);
    this.props.handleSetContent(null,page).then( () => {
      this.props.closeDrawer();
    });
  }

  handleUserMenuToggle = () => {
    this.setState((prevState:any) => ({
      userMenuOpen: !prevState.userMenuOpen
    }));
  }

  handleLogout = () => {
    this.setState({ userMenuOpen: false });
    this.props.handleSetContent(null, 'logout').then(() => {
      this.props.closeDrawer();
    });
  }

  render(){
    const { data, navDrawerOpen, closeDrawer } = this.props;
    const { userMenuOpen } = this.state;
    const unm = (data.bd||{}).unm; 

    const avatarsrc = `/api/photo/sample.jpg`;
    const logosrc = `/api/logo/logo.png`;

    return (
      <StyledDrawer
        variant="persistent"
        open={navDrawerOpen}
        onClose={closeDrawer}
      >
        <DrawerContent>
          {/* Header with Logo and Close Icon */}
          <DrawerHeader>
            <LogoContainer>
              <img src={logosrc} alt="logo" />
            </LogoContainer>
            <IconButton 
              onClick={closeDrawer}
              size="small"
              sx={{ 
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </DrawerHeader>
          
          {/* Menu Content */}
          <MenuContainer>
            <DynamicMenu data={data} handleClick={this.handleClick}/>
          </MenuContainer>

          {/* User Section at Bottom */}
          <UserSection>
            <UserButton onClick={this.handleUserMenuToggle}>
              <UserInfo>
                <Avatar 
                  src={avatarsrc}
                  sx={{ 
                    width: 36, 
                    height: 36,
                    border: '2px solid',
                    borderColor: 'divider'
                  }}
                />
                {navDrawerOpen && (
                  <UserName variant="body2">
                    {this.props.username}
                  </UserName>
                )}
              </UserInfo>
              {navDrawerOpen && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {userMenuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>
              )}
            </UserButton>
            
            {navDrawerOpen && (
              <Collapse in={userMenuOpen} timeout="auto" unmountOnExit>
                <LogoutButton onClick={this.handleLogout}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Logout"
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { fontWeight: 500 }
                    }}
                  />
                </LogoutButton>
              </Collapse>
            )}
          </UserSection>
        </DrawerContent>
      </StyledDrawer>
    );
  }
}

LeftDrawer.propTypes = {
  navDrawerOpen: PropTypes.bool,
  menus: PropTypes.array,
  username: PropTypes.string,
  data: PropTypes.object,
  handleSetContent:PropTypes.func
};

export default LeftDrawer;
